import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, Chip, Stack, TextField, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSearchParams } from "react-router-dom";

import { readProposal, type ProposalJob } from "../../api/proposal";
import { useIdentity } from "../../auth/useIdentity";
import { PromptPlans } from "./PromptPlans";
import { Result, Working } from "./ProposePriceList";
import { listSellerGst, speaksGst } from "../../api/priceList";

const POLL_MS = 2000;

/**
 * Plans › Create plan from prompt (owner 2026-11-02). Type new plans; the AI reads
 * the values, the software calculates, and the same preview and Approve as a
 * document follow. Nothing is created until a person approves it.
 */
export function CreatePlanFromPrompt() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  // The job id lives in the address, so a reload comes back to the same preview.
  const [params, setParams] = useSearchParams();
  const [ns, setNs] = useState(params.get("ns") || own);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const jobId = params.get("job");
  const setJobId = (id: string | null) =>
    setParams((p) => {
      const q = new URLSearchParams(p);
      if (id) {
        q.set("job", id);
        q.set("ns", ns);
      } else {
        q.delete("job");
      }
      return q;
    }, { replace: true });
  const sellerGst = useQuery({
    queryKey: ["pricelist-gst", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => listSellerGst(identity, ns, signal),
  });
  const job = useQuery<ProposalJob>({
    queryKey: ["proposal", ns, jobId],
    enabled: Boolean(identity.token && jobId),
    retry: false,
    queryFn: ({ signal }) => readProposal(identity, ns, jobId!, signal),
    refetchInterval: (q) =>
      q.state.data?.status === "running" || q.state.data?.apply?.status === "running" || !q.state.data ? POLL_MS : false,
  });

  return (
    <Box sx={{ p: 2, maxWidth: 1000 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Create plan from prompt <Chip size="small" color="secondary" icon={<AutoAwesomeIcon />} label="AI" />
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2, mt: 2 }}>
        <TextField size="small" label="Namespace" value={ns} sx={{ minWidth: 300 }}
          onChange={(e) => { setNs(e.target.value.trim().toLowerCase()); setJobId(null); }} />
        <TextField size="small" type="date" label="Prices take effect" required value={effectiveFrom}
          slotProps={{ inputLabel: { shrink: true } }} onChange={(e) => setEffectiveFrom(e.target.value)} />
      </Stack>
      <PromptPlans ns={ns} effectiveFrom={effectiveFrom} speak={speaksGst(sellerGst.data)} job={job.data}
        onStarted={(j) => { setStartedAt(Date.now()); setJobId(j.id); }} />
      {job.isError && <Alert severity="warning" sx={{ mb: 2 }}>This preview is no longer held. Nothing was created from it; type the plans again.</Alert>}
      {jobId && !job.isError && (!job.data || job.data.status === "running") && (
        <Working label="Reading what you typed" since={startedAt} hint="usually a few seconds" />
      )}
      {job.data && job.data.status !== "running" && (
        <Result job={job.data} onApplyStarted={() => void job.refetch()} onDiscard={() => setJobId(null)} />
      )}
    </Box>
  );
}
